import React, { ComponentType, Fragment } from 'react';
import { compose } from 'recompose';
import Auth from '@aws-amplify/auth';
import { Trans } from 'react-i18next';
import { ForgotPassword as AmplifyForgotPassword } from 'aws-amplify-react/lib/Auth';
import { Button, Typography } from '@material-ui/core';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Formik, Form } from 'formik';

import { AuthState, ICognitoError } from 'types/aws-amplify';
import { Login } from 'Links';
import { login } from 'paths';
import { withClient, withNotify, IWithClient, IWithNotify } from 'providers';
import {
  ActionContainer,
  ClientLogo,
  Logo,
  SubmitButton,
  Layouts,
  ValidatedTextField,
  FormikEffect,
  VisibilityAdornment,
} from 'components';

import { Main } from '../components';
import {
  ForgotPasswordSendSchema,
  ForgotPasswordSubmitSchema,
  IForgotPasswordSendValues,
  IForgotPasswordSubmitValues,
} from '../validations';

interface IPropsEnhanced extends IWithClient, IWithNotify, RouteComponentProps {}

interface IState {
  delivery: any;
  working: boolean;
  passwordVisible: boolean;
}

const enhancers = compose<IPropsEnhanced, {}>(withClient, withRouter, withNotify);

class ForgotPassword extends AmplifyForgotPassword<IPropsEnhanced, IState> {
  state: IState = {
    delivery: null,
    working: false,
    passwordVisible: false,
  };

  error = (error: ICognitoError) => {
    const { notify } = this.props;

    const message = this.errorMessage(error);
    this.setState({ working: false });
    notify.error({ message });
  };

  handleChange = ({ username }: IForgotPasswordSendValues) => {
    const { updateUsername } = this.props;
    updateUsername(username.toLowerCase());
  };

  handleSubmit = (values: IForgotPasswordSubmitValues) => {
    Object.assign(this.inputs, values);

    const { history } = this.props;
    const { username, code, password } = this.inputs;
    this.setState({ working: true });

    Auth.forgotPasswordSubmit(username.toLowerCase(), code, password)
      .then(() => {
        this.changeState(AuthState.signIn);
        this.setState({ delivery: null, working: false });
      })
      .then(() => history.push(login))
      .catch(this.error);
  };

  handleSend = (values: IForgotPasswordSendValues) => {
    Object.assign(this.inputs, values);

    const { username } = this.inputs;
    this.setState({ working: true });

    Auth.forgotPassword(username.toLowerCase())
      .then(data => {
        const delivery = data.CodeDeliveryDetails;
        this.setState({ delivery, working: false });
      })
      .catch(this.error);
  };

  renderActions() {
    const { delivery, working } = this.state;
    const i18nKey = delivery ? 'auth:forgotPasswordSubmitButton' : 'auth:forgotPasswordSendButton';

    return (
      <ActionContainer>
        <SubmitButton working={working} text={<Trans i18nKey={i18nKey} />} />
        <Button component={Login} fullWidth>
          <Trans i18nKey="auth:backToLogin" />
        </Button>
      </ActionContainer>
    );
  }

  renderSubmitView() {
    const { passwordVisible } = this.state;
    const initialValues: IForgotPasswordSubmitValues = {
      code: '',
      password: '',
    };

    return (
      <Formik
        key="submit"
        onSubmit={this.handleSubmit}
        initialValues={initialValues}
        validationSchema={ForgotPasswordSubmitSchema}>
        {() => (
          <Form>
            <ValidatedTextField name="code" i18nKey="auth:forgotPasswordCode" autoComplete="off" />
            <ValidatedTextField
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              i18nKey="auth:forgotPasswordNewPassword"
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <VisibilityAdornment
                    visible={passwordVisible}
                    onToggle={() => this.setState({ passwordVisible: !passwordVisible })}
                  />
                ),
              }}
            />
            {this.renderActions()}
          </Form>
        )}
      </Formik>
    );
  }

  renderSendView() {
    const { authData } = this.props;

    const initialValues: IForgotPasswordSendValues = {
      username: authData.username,
    };

    return (
      <Formik
        key="send"
        onSubmit={this.handleSend}
        initialValues={initialValues}
        validationSchema={ForgotPasswordSendSchema}>
        {() => (
          <Form>
            <FormikEffect onChange={this.handleChange} delay={100} />
            <ValidatedTextField name="username" i18nKey="user:email" />
            {this.renderActions()}
          </Form>
        )}
      </Formik>
    );
  }

  renderSidebar() {
    const { client } = this.props;
    const { delivery } = this.state;

    const LogoComponent: ComponentType = client ? ClientLogo : Logo;

    return (
      <Fragment>
        <LogoComponent />
        <Typography gutterBottom variant="h4" color="textSecondary">
          <Trans i18nKey="auth:forgotPasswordSidebarHeader" />
        </Typography>

        {delivery ? this.renderSubmitView() : this.renderSendView()}
      </Fragment>
    );
  }

  showComponent() {
    return (
      <Layouts.Full2Column
        sidebar={this.renderSidebar()}
        main={<Main headerI18nKey="auth:bodyHeader1" subHeaderI18nKey="auth:signInSubHeader" />}
      />
    );
  }
}

// @ts-ignore - TODO: AWS Components are untyped
export default enhancers(ForgotPassword);
